import { getEqlFieldFromFieldStringValue } from '@modules/common/utils';
import { useFetchMeasuredAsCriteria } from '@modules/quantify/hooks/useFetchMeasuredAsCriteria';
import { isAggregateFormula } from '@modules/quantify/utils';
import { EqlField, EqlFieldValueType, ObjectType } from 'generated/graphql';
import { useMemo } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface useGetSelectedMeasuredAsCriteriaOptionParams {
  measuredAsCriteria: string | undefined;
  formulaID: EQLFunctionName;
  groupBy: EqlField | undefined;
  shouldSkip?: boolean;
}

export const useGetSelectedMeasuredAsCriteriaOption = ({
  formulaID,
  groupBy,
  measuredAsCriteria,
  shouldSkip,
}: useGetSelectedMeasuredAsCriteriaOptionParams) => {
  const { criteriaList, loading } = useFetchMeasuredAsCriteria({
    field: isAggregateFormula(formulaID) ? groupBy : undefined,
    shouldSkip,
  });

  const selectedMeasuredAsCriteriaOption = useMemo((): EqlField | undefined => {
    if (!!measuredAsCriteria) {
      return (
        criteriaList.find(
          (eqlField) =>
            eqlField.field ===
            getEqlFieldFromFieldStringValue(measuredAsCriteria),
        ) ?? undefined
      );
    }

    // Set default value for measuredAsCriteria if it is not set
    if (groupBy && formulaID) {
      if (isAggregateFormula(formulaID)) {
        return groupBy;
      }

      if (
        formulaID === EQLFunctionName.Count ||
        formulaID === EQLFunctionName.NormaliseByPercent
      ) {
        return criteriaList.find(
          (criteria) =>
            criteria.objectType === ObjectType.Feedback &&
            criteria.valueType === EqlFieldValueType.None,
        );
      }
    }

    return undefined;
  }, [groupBy, formulaID, measuredAsCriteria, criteriaList]);

  // TODO(aryanshridhar): We had condition of isLoading as
  // selectedMeasuredAsCriteriaOption ? false : loading, which was causing infinite
  // loop in StrictMode. This is because of unMount, useEffect hooks changing
  // metadata which alters the value of isLoading causing new useEffect run.
  return {
    isLoading: loading,
    selectedMeasuredAsCriteriaOption,
  };
};
